import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { FolderPicker } from "@/components/FolderPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FolderCheck, FolderX, Trash2, Folder, RefreshCw, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", box_folder_id: "", box_folder_name: "" });

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const syncFromBox = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/projects/sync-from-box");
      toast.success(`Detected ${data.total_folders} folder(s) · added ${data.created} new project(s)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const save = async () => {
    if (!form.name || !form.code) { toast.error("Name and code are required"); return; }
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false); setForm({ name: "", code: "", box_folder_id: "", box_folder_name: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/projects/${id}`); load(); toast.success("Project deleted"); };

  const saveEdit = async () => {
    if (!editing.name || !editing.code) { toast.error("Name and code are required"); return; }
    try {
      await api.put(`/projects/${editing.id}`, {
        name: editing.name, code: editing.code,
        box_folder_id: editing.box_folder_id, box_folder_name: editing.box_folder_name,
      });
      toast.success("Project updated");
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader title="Projects" description="Auto-detected from your Box Projects Root, or add manually"
        actions={
          <div className="flex gap-2">
          <Button variant="outline" onClick={syncFromBox} disabled={syncing} data-testid="sync-projects-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Detecting…" : "Sync from Box"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-project-btn"><Plus className="h-4 w-4 mr-1" />New project</Button></DialogTrigger>
            <DialogContent data-testid="project-dialog">
              <DialogHeader><DialogTitle>New project</DialogTitle>
                <DialogDescription>Create a project and map it to a Box destination folder.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Project name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="project-name" placeholder="Riverside Tower" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Project code</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5" data-testid="project-code" placeholder="RVT-2026" />
                  <p className="text-xs text-muted-foreground mt-1">Used in ITB subject lines &amp; to match incoming replies.</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Box folder</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 text-sm border border-border rounded-md px-3 py-2 truncate">
                      {form.box_folder_name || <span className="text-muted-foreground">No folder selected</span>}
                    </div>
                    <Button type="button" variant="outline" onClick={() => setPickerOpen(true)} data-testid="pick-folder-btn">
                      <Folder className="h-4 w-4 mr-1" />Browse
                    </Button>
                  </div>
                </div>
                <Button onClick={save} className="w-full" data-testid="save-project-btn">Create project</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        } />
      <div className="p-8">
        <Card className="border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead><TableHead>Code</TableHead>
                <TableHead>Box folder</TableHead><TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">No projects yet. Use “Sync from Box” to auto-detect, or add one manually.</TableCell></TableRow>
              )}
              {projects.map((p) => (
                <TableRow key={p.id} className="hover:bg-accent/50" data-testid={`project-row-${p.code}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell><Badge variant="secondary">{p.code}</Badge></TableCell>
                  <TableCell>
                    {p.box_folder_id ? (
                      <span className="flex items-center gap-1.5 text-sm"><FolderCheck className="h-4 w-4 text-emerald-600" />{p.box_folder_name}</span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><FolderX className="h-4 w-4" />Not mapped</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-project-${p.code}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(p.id)} data-testid={`delete-project-${p.code}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
      <FolderPicker open={pickerOpen} onOpenChange={setPickerOpen}
        onPick={(f) => { setForm({ ...form, box_folder_id: f.id, box_folder_name: f.name }); setPickerOpen(false); }} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-project-dialog">
          <DialogHeader><DialogTitle>Edit project</DialogTitle>
            <DialogDescription>Fix the project code or name. The code is what matches subcontractor replies.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Project name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5" data-testid="edit-project-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Project code</Label>
                <Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} className="mt-1.5" data-testid="edit-project-code" placeholder="e.g. 2607-009" />
                <p className="text-xs text-muted-foreground mt-1">Detected from the Box folder name. Subs referencing this code in their reply auto-route here.</p>
              </div>
              <div className="text-xs text-muted-foreground">Box folder: <span className="font-mono">{editing.box_folder_name || "—"}</span></div>
              <Button onClick={saveEdit} className="w-full" data-testid="save-edit-project-btn">Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
